import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, eur, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search as SearchIcon, Download, ChevronRight, Users, TrendingUp, RefreshCcw, Sparkles, MessageSquare, UserPlus, Layers, X, Plus, Minus } from "lucide-react";
import { STAGE_ICON, STAGE_LABEL, isVipEffective } from "@/lib/customerStage";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { WhatsAppTemplateFlow } from "@/components/admin/WhatsAppTemplateFlow";
import { CreateCustomerModal } from "@/components/CreateCustomerModal";
import { ListPickerModal } from "@/components/CustomerListsChips";

const MARKETING_DOT = { accepted: "🟢", rejected: "🔴", never: "⚪" };

function isBirthdaySoon(bs) { return !!bs; }

const SEGMENT_STYLE = {
  nuovo:      "bg-emerald-100 text-emerald-800 border-emerald-300",
  attivo:     "bg-sky-100 text-sky-800 border-sky-300",
  dormiente:  "bg-amber-100 text-amber-800 border-amber-300",
  vip:        "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-300",
};

const SEGMENT_LABEL = {
  nuovo: "Nuovo", attivo: "Attivo", dormiente: "Dormiente", vip: "VIP",
};

// P0-6 FASE 2G · Paginazione server-side identica a StaffCustomers: 50 righe
// per pagina, "Carica altri 50" appende in coda, "Mostra tutti" carica il
// resto del dataset filtrato in una singola richiesta (cap 5000 backend).
// TUTTI i filtri (q + segment + stage) sono applicati lato backend PRIMA di
// limit/offset — nessun filtro client-side sui soli record già caricati.
const PAGE_SIZE = 50;
const LOAD_ALL_CAP = 5000;

export default function AdminCustomers() {
  const { user } = useAuth();
  const canSendTemplates = hasPermission(user, "staff.messaging.templates.send");
  const canCreateCustomer = hasPermission(user, "crm.customers.write");
  const canWriteCrm = hasPermission(user, "crm.customers.write");
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [segment, setSegment] = useState("");
  const [stageFilter, setStageFilter] = useState("");   // seed|plant|tree|fire|""
  // P0-6 FASE 2G+ · Filtro lista CRM (server-side). Nullable.
  const [listFilter, setListFilter] = useState(null);   // {id, name, color, members_count}
  const [rows, setRows] = useState([]);
  // P0-6 FASE 2G+ · Selezione multipla per bulk add/remove lista.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkListPickerOpen, setBulkListPickerOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState("add");   // "add" | "remove"
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);

  // Conteggio autoritativo dal backend (total = intero CRM, filtered = match
  // dei filtri correnti). NON è calcolato contando le rows visibili.
  const [counts, setCounts] = useState({ total: null, filtered: null });

  // M2.5 · Template WhatsApp: TUTTO nel modal. Nessun coordinamento con
  // la lista CRM, nessun banner esterno, nessuna checkbox sulla pagina.
  const [templateFlowOpen, setTemplateFlowOpen] = useState(false);

  // Reset paginazione a ogni cambio filtro / refresh esplicito.
  useEffect(() => {
    setOffset(0);
    setHasMore(true);
    setRows([]);
    setSelectedIds(new Set());
  }, [q, segment, stageFilter, listFilter?.id, reloadTick]);

  // P0-6 FASE 2G+ · Carica listFilter da URL ?list_id=…
  useEffect(() => {
    const lid = searchParams.get("list_id");
    if (lid && (!listFilter || listFilter.id !== lid)) {
      api.get(`/admin/crm/lists/${lid}`).then((r) => setListFilter(r.data))
        .catch(() => setListFilter(null));
    } else if (!lid && listFilter) {
      setListFilter(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch della pagina corrente (offset=0 → sostituisce, offset>0 → append
  // con dedup su id). Cancella in caso di cambio filtri via AbortController.
  useEffect(() => {
    const ctrl = new AbortController();
    const isFirstPage = offset === 0;
    if (isFirstPage) setLoading(true); else setLoadingMore(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (segment) params.set("segment", segment);
    if (stageFilter) params.set("stage", stageFilter);
    if (listFilter?.id) params.set("list_id", listFilter.id);
    params.set("limit", String(PAGE_SIZE));
    params.set("offset", String(offset));
    api.get(`/admin/customers?${params.toString()}`, { signal: ctrl.signal })
      .then((r) => {
        const page = Array.isArray(r.data) ? r.data : (r.data?.rows || []);
        setRows((prev) => {
          if (isFirstPage) return page;
          const seen = new Set(prev.map((x) => x.id));
          const merged = prev.slice();
          for (const p of page) if (!seen.has(p.id)) merged.push(p);
          return merged;
        });
        setHasMore(page.length === PAGE_SIZE);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        toast.error(formatApiError(e));
      })
      .finally(() => {
        if (isFirstPage) setLoading(false); else setLoadingMore(false);
      });
    return () => ctrl.abort();
  }, [q, segment, stageFilter, listFilter?.id, offset, reloadTick]);

  // Analytics (tiles) — indipendente dai filtri, solo mount + refresh.
  useEffect(() => {
    const ctrl = new AbortController();
    api.get("/admin/customers/analytics?days=30", { signal: ctrl.signal })
      .then((r) => setAnalytics(r.data || null))
      .catch(() => {});
    return () => ctrl.abort();
  }, [reloadTick]);

  // Conteggi autoritativi (total + filtered) coerenti con i filtri attivi.
  useEffect(() => {
    const ctrl = new AbortController();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (segment) params.set("segment", segment);
    if (stageFilter) params.set("stage", stageFilter);
    if (listFilter?.id) params.set("list_id", listFilter.id);
    const url = params.toString()
      ? `/admin/customers/count?${params.toString()}`
      : "/admin/customers/count";
    api.get(url, { signal: ctrl.signal })
      .then((r) => {
        if (r?.data && typeof r.data.total === "number") {
          setCounts({
            total: r.data.total,
            filtered: typeof r.data.filtered === "number" ? r.data.filtered : r.data.total,
          });
        }
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, [q, segment, stageFilter, listFilter?.id, reloadTick]);

  // Refresh manuale + segnale globale post hard-delete.
  const refresh = useCallback(() => setReloadTick((t) => t + 1), []);
  useEffect(() => {
    const h = () => refresh();
    window.addEventListener("sb-customers-changed", h);
    return () => window.removeEventListener("sb-customers-changed", h);
  }, [refresh]);

  const doExport = async () => {
    try {
      const r = await api.get("/admin/customers/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "sarabi-customers.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV esportato");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const anonymize = async (id) => {
    if (!window.confirm("Anonimizzare permanentemente questo cliente?")) return;
    try {
      await api.post(`/admin/customers/${id}/anonymize`);
      refresh();
      toast.success("Cliente anonimizzato");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const total = analytics?.total_customers || 0;
  const active = analytics?.active_in_window || 0;
  const newIn = analytics?.new_in_window || 0;
  const retention = analytics?.retention_pct;
  const avgLtv = analytics?.avg_ltv_cents || 0;
  const avgVisits = analytics?.avg_visits || 0;

  const hasActiveFilter = !!(q.trim() || segment || stageFilter || listFilter?.id);

  const toggleRowSelected = (id) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const bulkAddToLists = async (listIds) => {
    const ids = Array.from(selectedIds);
    if (!ids.length || !listIds?.length) return;
    try {
      // Un endpoint per lista → mantiene atomicità per membership
      let total = 0, dup = 0;
      for (const lid of listIds) {
        const { data } = await api.post(`/admin/crm/lists/${lid}/members`,
                                          { customer_ids: ids });
        total += data.added || 0;
        dup += data.already_present || 0;
      }
      toast.success(`Aggiunti ${total} · già presenti ${dup}`);
      setSelectedIds(new Set());
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkRemoveFromLists = async (listIds) => {
    const ids = Array.from(selectedIds);
    if (!ids.length || !listIds?.length) return;
    try {
      let total = 0;
      for (const lid of listIds) {
        const { data } = await api.delete(`/admin/crm/lists/${lid}/members`,
                                            { data: { customer_ids: ids } });
        total += data.removed || 0;
      }
      toast.success(`Rimossi ${total} da ${listIds.length} liste`);
      setSelectedIds(new Set());
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="crm-page">
      <div className="flex items-center gap-2 flex-wrap">
        <div>
          <div className="eyebrow">CRM · Loyalty</div>
          <h1 className="font-serif text-3xl flex items-center gap-2">
            <Users className="w-7 h-7" /> Clienti
            {counts.total !== null && (
              <span
                data-testid="admin-cust-total-count"
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full
                           bg-bronze-50 border border-bronze-200 text-bronze-800
                           text-sm font-medium tabular-nums"
                title="Totale clienti presenti nel CRM (fonte: backend)"
              >
                {counts.total.toLocaleString("it-IT")}
                {hasActiveFilter && counts.filtered !== null && counts.filtered !== counts.total && (
                  <span className="text-bronze-600 font-normal">
                    {" · "}{counts.filtered.toLocaleString("it-IT")} filtrati
                  </span>
                )}
              </span>
            )}
          </h1>
        </div>
        <div className="ml-auto flex gap-2">
          <Link to="/admin/loyalty" className="btn-outline text-xs py-1 px-3 flex items-center gap-1"
                data-testid="crm-loyalty-link">
            <Sparkles className="w-3 h-3" /> Loyalty
          </Link>
          <Button variant="outline" size="sm" onClick={doExport}
                  data-testid="crm-export-csv">
            <Download className="w-3 h-3 mr-1" /> Esporta CSV
          </Button>
          {canCreateCustomer && (
            <Button size="sm" onClick={() => setCreateOpen(true)}
                    data-testid="btn-add-customer-admin"
                    className="bg-bronze-600 hover:bg-bronze-700 text-white">
              <UserPlus className="w-3 h-3 mr-1" /> Aggiungi cliente
            </Button>
          )}
          {canSendTemplates && (
            <Button variant="outline" size="sm"
                    onClick={() => setTemplateFlowOpen(true)}
                    data-testid="btn-open-templates"
                    className="border-emerald-400 text-emerald-800 hover:bg-emerald-50">
              <MessageSquare className="w-3 h-3 mr-1" /> Template WhatsApp
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={refresh} data-testid="crm-refresh">
            <RefreshCcw className="w-3 h-3 mr-1" /> Aggiorna
          </Button>
        </div>
      </div>

      {/* M2.5 · Template flow modal — TUTTO dentro il modal */}
      <WhatsAppTemplateFlow
        isOpen={templateFlowOpen}
        onClose={() => setTemplateFlowOpen(false)}
      />

      {/* Create Customer modal */}
      <CreateCustomerModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { refresh(); toast.success("Cliente aggiunto"); }}
        existingUrlBase="/admin/customers"
      />

      {/* Analytics tiles */}
      <section className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="crm-analytics">
        <div className="rounded-xl bg-white border border-sand-300 p-3">
          <div className="text-xs text-ink-500">Totale clienti</div>
          <div className="text-2xl font-serif" data-testid="crm-metric-total">{total}</div>
        </div>
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
          <div className="text-xs text-emerald-700">Nuovi (30 gg)</div>
          <div className="text-2xl font-serif text-emerald-800">{newIn}</div>
        </div>
        <div className="rounded-xl bg-sky-50 border border-sky-200 p-3">
          <div className="text-xs text-sky-700">Attivi (30 gg)</div>
          <div className="text-2xl font-serif text-sky-800">{active}</div>
        </div>
        <div className="rounded-xl bg-fuchsia-50 border border-fuchsia-200 p-3">
          <div className="text-xs text-fuchsia-800">Retention</div>
          <div className="text-2xl font-serif text-fuchsia-900">
            {retention === null ? "—" : `${retention ?? 0}%`}
          </div>
        </div>
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
          <div className="text-xs text-amber-800">Visite medie</div>
          <div className="text-2xl font-serif text-amber-900">{avgVisits}</div>
        </div>
        <div className="rounded-xl bg-bronze-50 border border-bronze-200 p-3">
          <div className="text-xs text-bronze-700 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> LTV medio
          </div>
          <div className="text-2xl font-serif text-bronze-900">{eur(avgLtv)}</div>
        </div>
      </section>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-sm flex-1">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Cerca nome, telefono, email…"
                 className="pl-9" data-testid="cust-search" />
        </div>
        {/* P0-6 FASE 2G+ · Filtro Lista CRM */}
        {listFilter ? (
          <span data-testid="active-list-filter"
                className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1 border font-medium"
                style={{ borderColor: listFilter.color || "#94a3b8",
                          background: (listFilter.color || "#94a3b8") + "1a",
                          color: listFilter.color || "#1e293b" }}>
            <Layers className="w-3 h-3" />
            Lista: {listFilter.name}
            <button onClick={() => { setListFilter(null); setSearchParams({}); }}
                     data-testid="clear-list-filter"
                     className="opacity-60 hover:opacity-100">
              <X className="w-3 h-3" />
            </button>
          </span>
        ) : (
          <Link to="/admin/crm/lists"
                 data-testid="btn-open-lists-page"
                 className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1 border border-ink-200 text-ink-700 hover:bg-ink-50">
            <Layers className="w-3 h-3" /> Liste
          </Link>
        )}
        <div className="flex items-center gap-1 flex-wrap">
          {["", "nuovo", "attivo", "dormiente", "vip"].map((s) => (
            <button key={s || "all"} onClick={() => setSegment(s)}
                    data-testid={`crm-segment-${s || "all"}`}
                    className={`text-xs rounded-full border px-3 py-1
                                ${segment === s ? "bg-ink-900 text-white border-ink-900"
                                                : "bg-white border-ink-200 hover:bg-ink-50"}`}>
              {s ? SEGMENT_LABEL[s] : "Tutti"}
            </button>
          ))}
          <span className="text-ink-400 mx-1">·</span>
          {["seed", "plant", "tree", "fire"].map((s) => (
            <button key={s} onClick={() => setStageFilter(stageFilter === s ? "" : s)}
                    data-testid={`crm-stage-${s}`}
                    title={STAGE_LABEL[s]}
                    className={`text-xs rounded-full border px-3 py-1
                                ${stageFilter === s ? "bg-emerald-700 text-white border-emerald-700"
                                                    : "bg-white border-ink-200 hover:bg-ink-50"}`}>
              {STAGE_ICON[s]} {STAGE_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Top spenders */}
      {analytics?.top_spenders?.length > 0 && (
        <section className="rounded-2xl border border-bronze-200 bg-bronze-50/40 p-3">
          <div className="text-xs font-semibold text-bronze-800 mb-2">🏆 Top spenders</div>
          <div className="flex gap-2 flex-wrap">
            {analytics.top_spenders.slice(0, 5).map((c) => (
              <Link key={c.id} to={`/admin/customers/${c.id}`}
                    className="text-xs rounded-full bg-white border border-bronze-200 px-3 py-1 hover:bg-bronze-100"
                    data-testid={`crm-top-${c.id}`}>
                {c.name} · <b>{eur(c.total_spent_cents)}</b>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* P0-6 FASE 2G+ · Bulk actions bar (visible only when rows selected) */}
      {canWriteCrm && selectedIds.size > 0 && (
        <div data-testid="bulk-actions-bar"
              className="sticky top-0 z-10 rounded-2xl border-2 border-bronze-400 bg-bronze-50 p-3 flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-bronze-900">
            <b data-testid="bulk-selected-count">{selectedIds.size}</b> selezionati
          </span>
          <Button size="sm"
                   onClick={() => { setBulkMode("add"); setBulkListPickerOpen(true); }}
                   data-testid="btn-bulk-add-list"
                   className="bg-bronze-600 hover:bg-bronze-700 text-white rounded-full">
            <Plus className="w-3 h-3 mr-1" /> Aggiungi a lista
          </Button>
          <Button size="sm" variant="outline"
                   onClick={() => { setBulkMode("remove"); setBulkListPickerOpen(true); }}
                   data-testid="btn-bulk-remove-list"
                   className="rounded-full">
            <Minus className="w-3 h-3 mr-1" /> Rimuovi da lista
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())}
                   data-testid="btn-bulk-deselect" className="rounded-full ml-auto">
            Deseleziona
          </Button>
        </div>
      )}
      {bulkListPickerOpen && (
        <ListPickerModal
          title={bulkMode === "add" ? `Aggiungi ${selectedIds.size} clienti a…` : `Rimuovi ${selectedIds.size} clienti da…`}
          multiSelect
          onClose={() => setBulkListPickerOpen(false)}
          onMultiSelect={(ids) => {
            if (bulkMode === "add") bulkAddToLists(ids);
            else bulkRemoveFromLists(ids);
          }}
        />
      )}

      {/* Table */}
      {loading ? (
        <div className="p-6 text-ink-500" data-testid="crm-loading">Caricamento…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-sand-300 p-8 text-center text-sm text-ink-500"
             data-testid="crm-empty">
          Nessun cliente corrisponde ai filtri correnti.
        </div>
      ) : (
        <div className="rounded-2xl border border-sand-300 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-ink-500 bg-sand-50">
              <tr>
                {canWriteCrm && <th className="p-3 w-8"></th>}
                <th className="text-left p-3">Nome</th>
                <th>Telefono</th>
                <th>Segmento</th>
                <th>Ordini</th>
                <th>Speso</th>
                <th>Ø ticket</th>
                <th>Ultima visita</th>
                <th>Punti</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-sand-200 hover:bg-sand-50"
                    data-testid={`cust-${r.id}`}>
                  {canWriteCrm && (
                    <td className="p-3">
                      <input type="checkbox"
                              checked={selectedIds.has(r.id)}
                              onChange={() => toggleRowSelected(r.id)}
                              data-testid={`cust-check-${r.id}`}
                              onClick={(e) => e.stopPropagation()} />
                    </td>
                  )}
                  <td className="p-3 font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      {r.stage && (
                        <span title={STAGE_LABEL[r.stage]} data-testid={`cust-${r.id}-stage`}
                              className="leading-none">{STAGE_ICON[r.stage]}</span>
                      )}
                      {isVipEffective(r) && (
                        <span title="VIP" className="text-amber-500 leading-none"
                              data-testid={`cust-${r.id}-vip`}>⭐</span>
                      )}
                      <span title={`Marketing: ${r.marketing_state}`}
                            data-testid={`cust-${r.id}-marketing`}
                            className="leading-none text-[11px]">
                        {MARKETING_DOT[r.marketing_state] || "⚪"}
                      </span>
                      {isBirthdaySoon(r.birthday_soon) && (
                        <span title={r.birthday_soon.is_today ? "Compleanno oggi!" : `Compleanno tra ${r.birthday_soon.days_until} gg`}
                              className="leading-none"
                              data-testid={`cust-${r.id}-birthday`}>🎂</span>
                      )}
                      <span>{[r.name, r.surname].filter(Boolean).join(" ") || "—"}</span>
                    </span>
                  </td>
                  <td className="text-center">{r.phone || "-"}</td>
                  <td className="text-center">
                    <span className={`text-[10px] uppercase tracking-wider rounded-full border px-2 py-0.5 ${SEGMENT_STYLE[r.segment] || SEGMENT_STYLE.attivo}`}>
                      {SEGMENT_LABEL[r.segment] || r.segment}
                    </span>
                  </td>
                  <td className="text-center">{r.orders_count}</td>
                  <td className="text-center">{eur(r.total_spent_cents)}</td>
                  <td className="text-center text-ink-500">{eur(r.avg_ticket_cents)}</td>
                  <td className="text-center text-ink-500">
                    {r.last_order_at?.slice(0, 10) || "-"}
                    {r.days_since_last_visit !== null && (
                      <div className="text-[10px]">{r.days_since_last_visit} gg fa</div>
                    )}
                  </td>
                  <td className="text-center font-medium text-bronze-800">
                    {r.loyalty_points}
                  </td>
                  <td className="text-right pr-2">
                    <Link to={`/admin/customers/${r.id}`}
                          className="inline-flex items-center gap-1 text-bronze-700 text-xs hover:underline"
                          data-testid={`view-${r.id}`}>
                      Profilo <ChevronRight className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* P0-6 FASE 2G · Paginazione: "Carica altri 50" per navigare l'intero
          CRM senza limiti di 100 righe. "Mostra tutti" carica in un'unica
          request il resto del dataset filtrato (rispetta q + segment + stage,
          cap 5000 lato backend). Al termine, banner di completamento. */}
      {!loading && rows.length > 0 && hasMore && (
        <div className="mt-4 flex justify-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            disabled={loadingMore || loadingAll}
            data-testid="admin-cust-load-more"
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
            className="border-bronze-400 text-bronze-800 hover:bg-bronze-50 rounded-full"
          >
            {loadingMore ? "Caricamento…" : `Carica altri ${PAGE_SIZE}`}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={loadingMore || loadingAll}
            data-testid="admin-cust-load-all"
            onClick={async () => {
              setLoadingAll(true);
              try {
                const params = new URLSearchParams();
                if (q) params.set("q", q);
                if (segment) params.set("segment", segment);
                if (stageFilter) params.set("stage", stageFilter);
                params.set("limit", String(LOAD_ALL_CAP));
                params.set("offset", String(rows.length));
                const r = await api.get(`/admin/customers?${params.toString()}`);
                const page = Array.isArray(r.data) ? r.data : (r.data?.rows || []);
                setRows((prev) => {
                  const seen = new Set(prev.map((x) => x.id));
                  const merged = prev.slice();
                  for (const p of page) if (!seen.has(p.id)) merged.push(p);
                  return merged;
                });
                // Vedi commento gemello in StaffCustomers.jsx: se il cap è
                // stato saturato ci possono essere altri record oltre; in
                // caso contrario il dataset è esaurito e NON aggiorniamo
                // `offset` per evitare che il useEffect di paginazione
                // richieda una pagina già caricata (che rimetterebbe
                // hasMore=true erroneamente).
                if (page.length >= LOAD_ALL_CAP) {
                  setHasMore(true);
                  setOffset((o) => o + page.length);
                } else {
                  setHasMore(false);
                }
              } catch (e) {
                toast.error(formatApiError(e));
              } finally {
                setLoadingAll(false);
              }
            }}
            className="border-bronze-600 bg-bronze-50 text-bronze-900 hover:bg-bronze-100 rounded-full font-medium"
          >
            {loadingAll ? "Caricamento…" : "Mostra tutti"}
          </Button>
        </div>
      )}
      {!loading && rows.length > 0 && !hasMore && (
        <div className="mt-4 text-center text-xs text-ink-400"
             data-testid="admin-cust-end">
          {hasActiveFilter
            ? <>Tutti i {rows.length.toLocaleString("it-IT")} clienti filtrati caricati</>
            : <>Tutti i {(counts.total ?? rows.length).toLocaleString("it-IT")} clienti caricati</>}
        </div>
      )}
    </div>
  );
}
